import { IconeHistorique } from '@/components/historique/icone-historique';
import { Button, Icon } from '@pms-digital/design-system';
import type { NoyauCode } from '@rosetta/shared';
import { MessageTahiti } from './message-tahiti';

interface BandeauPartielProps {
  /** The chosen noyaux, in charter order. */
  noyaux: readonly NoyauCode[];
  /** Those whose translation did not succeed. */
  enEchec: readonly NoyauCode[];
  /** The message of Tahiti, when it is readable. */
  messageErreur: string | undefined;
  /** Opens the input screen with this text and the noyaux that did not succeed. */
  onRelancer: () => void;
}

/** "SGP", "SGP et GSP", "SGP, PSG et GSP". */
function enumerer(codes: readonly NoyauCode[]): string {
  if (codes.length <= 1) return codes.join('');
  return `${codes.slice(0, -1).join(', ')} et ${codes.at(-1)}`;
}

/** "2 traductions sur 3 sont prêtes.", "1 traduction sur 3 est prête." */
export function phrasePartielle(pretes: number, total: number): string {
  return pretes > 1
    ? `${pretes} traductions sur ${total} sont prêtes.`
    : `${pretes} traduction sur ${total} est prête.`;
}

/**
 * The banner over the tabs of a partial translation (#13): how many translations are ready, the
 * noyaux that did not succeed, the message of Tahiti when it is readable, and "relancer", which
 * opens the input screen with this text and those noyaux only (the ready ones are not paid twice).
 */
export function BandeauPartiel({
  noyaux,
  enEchec,
  messageErreur,
  onRelancer,
}: BandeauPartielProps) {
  const echoues = enumerer(enEchec);
  return (
    <div className="mt-6 flex flex-wrap items-start justify-between gap-4 rounded-md border-rouge-deep border-l-4 bg-gray-50 px-6 py-4">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-px inline-flex shrink-0 text-rouge-deep">
          <Icon name="error" size={24} />
        </span>
        <div className="flex min-w-0 flex-col gap-2">
          <p className="font-semibold text-body leading-[1.4]">
            {phrasePartielle(noyaux.length - enEchec.length, noyaux.length)}
          </p>
          <p className="text-gray-700 text-small leading-[1.5]">
            {enEchec.length > 1
              ? `Les traductions vers ${echoues} n'ont pas abouti.`
              : `La traduction vers ${echoues} n'a pas abouti.`}
          </p>
          {messageErreur === undefined ? null : <MessageTahiti message={messageErreur} />}
        </div>
      </div>
      <Button type="button" variant="primary" onClick={onRelancer}>
        <IconeHistorique name="refresh" size={20} />
        relancer {echoues}
      </Button>
    </div>
  );
}
